import useI18n from '@/hooks/useI18n';
import { useContext } from 'react';
import { UserContext } from '@/context/UserContext';
import { useBrandSentimentQuery } from '@/hooks/queries/useAnalyticsQueries';
import SegmentedBar from './SegmentedBar';
import { formatPercent } from '@/utils/numberUtils';

interface KeywordInsight {
  keyword: string;
  meaning: string;
  occurrences: number;
}

interface BrandSentimentsComponentProps {
  chartFilter?: string;
  customStartDate?: string;
  customEndDate?: string;
}

const BrandSentimentsComponent = ({
  chartFilter,
  customStartDate,
  customEndDate,
}: BrandSentimentsComponentProps) => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;

  const brandId = user?.role === 'brandUser' ? user.brand?._id || '' : '';
  const isRealUser = !!brandId && !isDummyUser;

  const { data: sentimentData, isLoading } = useBrandSentimentQuery(
    brandId,
    isRealUser,
    chartFilter,
    customStartDate,
    customEndDate,
  );

  const positivePercentage = isDummyUser
    ? 72
    : parseFloat(sentimentData?.positiveRatio || '0');
  const negativePercentage = isDummyUser
    ? 28
    : parseFloat(sentimentData?.negativeRatio || '0');
  const topPositiveKeywords: KeywordInsight[] = isDummyUser
    ? [
        {
          keyword: 'amazing',
          meaning: 'Expressing strong approval',
          occurrences: 34,
        },
        {
          keyword: 'quality',
          meaning: 'High standard of product',
          occurrences: 27,
        },
        {
          keyword: 'love',
          meaning: 'Strong positive feeling',
          occurrences: 21,
        },
        {
          keyword: 'recommend',
          meaning: 'Willingness to endorse to others',
          occurrences: 18,
        },
        {
          keyword: 'trusted',
          meaning: 'Perceived brand reliability',
          occurrences: 15,
        },
        {
          keyword: 'value',
          meaning: 'Good return for price paid',
          occurrences: 11,
        },
      ]
    : (sentimentData?.topPositiveKeywords || []).slice(0, 5);
  const topNegativeKeywords: KeywordInsight[] = isDummyUser
    ? [
        {
          keyword: 'expensive',
          meaning: 'High price concern',
          occurrences: 12,
        },
        {
          keyword: 'slow',
          meaning: 'Delivery or performance issue',
          occurrences: 8,
        },
        {
          keyword: 'confusing',
          meaning: 'Unclear product or process',
          occurrences: 5,
        },
      ]
    : (sentimentData?.topNegativeKeywords || []).slice(0, 5);

  const hasData = isDummyUser
    ? true
    : !!(
        sentimentData &&
        sentimentData.positiveCount + sentimentData.negativeCount > 0
      );

  return (
    <div className="">
      <h2 className="text-sm font-medium text-[#121111] opacity-90 mb-4">
        {t('common.brandSentiments')}
      </h2>

      {isLoading ? (
        <div className="flex justify-center items-center h-[120px]">
          <span className="text-xl font-medium text-[#9B919D]">
            {String(t('common.loading'))}
          </span>
        </div>
      ) : !hasData ? (
        <div className="flex justify-center items-center h-[120px] border border-[#E5E9EB] rounded-3xl">
          <span className="text-xl font-medium text-[#9B919D]">
            {String(t('common.noData'))}
          </span>
        </div>
      ) : (
        <div className="flex gap-4">
          {/* Progress bar panel */}
          <div className="border border-[#E3E7E9] rounded-3xl w-1/2 p-6 flex items-center">
            <div className="flex-1 mt-[33px]">
              <div className="flex flex-col gap-2">
                <div className="relative flex items-center gap-1">
                  <div className="bg-white px-3 py-1.5 rounded-[20px] flex-1">
                    <div className="flex items-center relative">
                      <SegmentedBar
                        height={12}
                        gap={6}
                        ariaLabel={`Sentiment: ${formatPercent(positivePercentage)} positive, ${formatPercent(negativePercentage)} negative`}
                        segments={[
                          { color: '#78B159', percent: positivePercentage },
                          { color: '#FF6A69', percent: negativePercentage },
                        ]}
                      />
                      <div
                        className="absolute top-0 px-[10px] py-[3px] bg-[#78B159] rounded-full text-white text-xs font-medium whitespace-nowrap"
                        style={{
                          left: `${positivePercentage}%`,
                          top: -33,
                          transform: 'translateX(-50%) translateY(-50%)',
                        }}
                      >
                        <img
                          src="/assets/DarkGreenPolygon.svg"
                          className="absolute bottom-[-7px] left-1/2 -translate-x-1/2"
                        />
                        {`${formatPercent(positivePercentage)} Positive`}
                      </div>
                      <div
                        className="absolute h-[32px]"
                        style={{
                          left: `calc(${positivePercentage - 0.3}%)`,
                          top: '50%',
                          transform: 'translateX(-50%) translateY(-50%)',
                          borderLeft: '2px dashed #113E53',
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <p className="text-sm font-normal text-[#9B919D]">
                    {t('common.positive')} {formatPercent(positivePercentage)}
                  </p>
                  <p className="text-sm font-normal text-[#9B919D]">
                    {t('common.negative')} {formatPercent(negativePercentage)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Keywords panel */}
          <div className="border border-[#E3E7E9] rounded-3xl w-1/2 p-6 flex flex-col justify-center gap-4">
            {/* Label on the left (140px), chips inline on the right (Figma). */}
            <div className="flex items-center gap-6">
              <p
                className="text-sm text-[#9B919D] shrink-0"
                style={{ width: 140 }}
              >
                {t('common.topPositiveWord')}
              </p>
              {topPositiveKeywords.length > 0 ? (
                <div className="flex flex-wrap gap-2 flex-1">
                  {topPositiveKeywords.map((kw) => (
                    <span
                      key={kw.keyword}
                      title={kw.meaning}
                      className="text-xs font-semibold text-[#0FAF62] px-2 py-1 bg-[#E0F5E7] rounded-full"
                    >
                      {kw.keyword}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#9B919D]">—</p>
              )}
            </div>
            <div className="flex items-center gap-6">
              <p
                className="text-sm text-[#9B919D] shrink-0"
                style={{ width: 140 }}
              >
                {t('common.topNegativeWord')}
              </p>
              {topNegativeKeywords.length > 0 ? (
                <div className="flex flex-wrap gap-2 flex-1">
                  {topNegativeKeywords.map((kw) => (
                    <span
                      key={kw.keyword}
                      title={kw.meaning}
                      className="text-xs font-semibold text-[#FF6A69] bg-[#FF6A69]/20 px-2 py-1 rounded-full"
                    >
                      {kw.keyword}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#9B919D]">—</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandSentimentsComponent;
